/**
 * Shared page furniture for the statically-generated landing pages.
 *
 * Every gen-*.mjs script used to carry its own verbatim copy of the <head>
 * links, the header bar and the footer, so a design change meant editing six
 * files and hoping they stayed in step. They now import from here, which is
 * also what keeps the ~1,100 generated pages looking like the same site as
 * the SPA.
 *
 * The class vocabulary below mirrors src/styles.css: a cool paper ground,
 * white sheets lifted on soft shadows, 4 px corners with pills for chips, and
 * colour split by job — blue acts, red carries the figures. Keep the two in
 * step.
 */

/** Font links. Mirrors index.html — a refined Japanese gothic for everything
 *  set in Japanese, and a geometric grotesk for every figure. Display and body
 *  are separated by size, weight and tracking rather than by a third face. */
export const fontLinks = `<link rel="preconnect" href="https://fonts.googleapis.com" />
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin />
<link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700&family=Zen+Kaku+Gothic+New:wght@400;500;700&display=swap" />`;

export const themeColor = '<meta name="theme-color" content="#0b74c4" />';

/** Class vocabulary. Generators compose these instead of naming raw colours. */
export const C = {
  body: 'bg-paper text-ink min-h-screen font-body leading-jp tracking-copy',

  /* The site's one measure. The header and every page's main share it, so the
     wordmark sits directly above the first line of content instead of out at
     the window edge.

     The cap used to be 6xl (1152px), which on a 1920px desktop left 384px of
     dead paper on either side and used 60% of the window — the wrong trade
     for a page whose main objects are a map, a seven-column table and a
     time-series chart. 1680 fills a 1440px laptop outright and leaves a
     deliberate margin on a 1920, and it is a real bound rather than none:
     past about 1800px the ranking table stops reading as rows, because the
     price ends up hundreds of pixels from the ward it belongs to. The side
     padding grows with the viewport so the content keeps a margin instead of
     running into the window edge.

     Running text does not follow the page width — see `prose`. */
  shell: 'mx-auto w-full max-w-[1680px] px-6 sm:px-10 lg:px-14 2xl:px-20',

  /* Reading measure. A line of Japanese set across a 1900px window is
     unreadable, so prose keeps its own cap and is left-aligned inside the
     shell, starting under the heading. */
  prose: 'max-w-3xl',

  /* Document measure, for the pages that are nothing but text: about and
     how-it-works. The data pages hang wide tables and a chart off the left
     margin, so a narrow lead above them reads as deliberate. An info page has
     no wide element to balance it, and pinning its one column to the left of
     a 2560px window just looks abandoned — so the whole document, heading
     included, is centred instead. */
  doc: 'mx-auto max-w-4xl',

  h1: 'text-h1 font-bold text-ink',
  h2: 'text-h2 font-bold text-ink',
  /* A section heading in the reading flow. The bar is a ::before so the
     treatment travels with the class — most of the site's headings were bare
     `h2` because the bar used to live in markup only `sectionTitle()` emitted,
     and almost nothing called it. Headings inside the closing call to action
     stay bare: it is centred, and a left bar on centred text reads as a
     mistake. */
  sectionH2:
    'flex items-center gap-2 text-h2 font-bold text-ink before:h-5 before:w-1 before:flex-none before:rounded-sm before:bg-primary before:content-[\'\']',
  h3: 'text-[15px] font-bold text-ink',

  label: 'text-[11px] font-bold tracking-label text-ink-3',
  lead: 'text-[15px] leading-jp text-ink-2',
  body_: 'text-[14px] leading-jp text-ink-2',
  small: 'text-[12px] leading-jp text-ink-3',

  link: 'font-bold text-primary-text underline underline-offset-2 hover:text-primary-dark',
  linkPlain: 'font-bold text-ink-2 hover:text-primary-text',

  /* Cards are white and lifted, not outlined — that is what separates content
     from the ground in a colourful UI. */
  card: 'rounded-lg bg-sheet p-5 shadow-sm',
  /* The pill. One definition for the whole site: the estimator imports these
     strings the same way it imports the header, so a chip in the map panel and
     a chip on a ranking page are the same object. There were three variants
     before — the estimator's `.chip` rule, this one, and the ranking pages'
     inline classes, which differed in border tone, padding, size and (because
     the floor-plan chips carried `fig`) even in typeface: "1K" was set in
     Outfit on the map page and in the body gothic everywhere else. A floor
     plan is a label, not a figure, so it takes the body face. */
  tag: 'inline-flex items-center rounded-pill border border-rule-strong bg-sheet px-3.5 py-1.5 text-[12px] font-bold leading-chip text-ink-2 transition-colors hover:border-primary hover:bg-primary hover:text-sheet',
  tagActive:
    'inline-flex items-center rounded-pill border border-primary bg-primary px-3.5 py-1.5 text-[12px] font-bold leading-chip text-sheet',

  fig: 'font-figure tabular-nums font-semibold',
  figLg: 'font-figure tabular-nums font-bold text-[26px] leading-none tracking-tight text-accent',

  /* Blue acts, red values. Giving both jobs to one colour makes the button
     and the price compete. */
  btn: 'inline-flex items-center justify-center rounded bg-primary px-8 py-3.5 text-[15px] font-bold tracking-wide text-sheet shadow transition-colors hover:bg-primary-dark',
  btnQuiet:
    'inline-flex items-center justify-center rounded border border-primary bg-sheet px-4 py-2 text-[12px] font-bold text-primary-text transition-colors hover:bg-primary hover:text-sheet',

  table: 'w-full text-[13px]',
  th: 'bg-sheet-sunk px-3 py-2.5 text-left text-[11px] font-bold tracking-label text-ink-3',
  thNum: 'bg-sheet-sunk px-3 py-2.5 text-right text-[11px] font-bold tracking-label text-ink-3',
  tbody: 'divide-y divide-rule',
  td: 'px-3 py-2.5 text-ink-2',
  /* The money column. */
  tdNum: 'px-3 py-2.5 text-right font-figure tabular-nums font-semibold text-ink',
  /* Right-aligned figures that are context rather than the answer — area,
     quarter, counts. Same tabular alignment, none of the weight, so the price
     column still wins the row. */
  tdMeta: 'px-3 py-2.5 text-right font-figure tabular-nums text-ink-3',
  /* Wrapper that makes a table one of the page's lifted white sheets. */
  tableWrap: 'mt-4 overflow-x-auto rounded-lg bg-sheet shadow-sm',

  section: 'mt-[clamp(2.5rem,4.5vw,4.5rem)]',
};

/** Section heading with the rule running out to the measure, matching the
 *  SPA's .section-title. Inlined because the static CSS has no components
 *  layer of its own. */
export function sectionTitle(text) {
  return `<h2 class="${C.sectionH2}">${text}</h2>`;
}

/**
 * Site chrome, as class strings rather than markup, because the two halves of
 * the site render it in different languages: the generators build HTML from
 * `siteHeader()` below, and the estimator builds the same bar in TSX. Both
 * import this object, so there is one definition and no way for them to drift.
 *
 * The header row is full-bleed — it hugs the viewport on every page rather
 * than lining up with the content column. That is the only rule that can hold
 * on both sides: the estimator is a two-pane app that fills the window, the
 * content pages are a measured column, and any inset that suited one put the
 * logo somewhere else on the other. Before this, the wordmark sat at x=28 on
 * the estimator and x=152 everywhere else, and moved as you navigated.
 *
 * The height is fixed rather than derived from the tallest child, so a page
 * whose nav happens to carry a button does not get a taller bar.
 */
export const HEADER = {
  bar: 'sticky top-0 z-30 border-b border-rule bg-sheet shadow-sm',
  row: 'flex h-14 items-center justify-between gap-4',
  brandWrap: 'flex items-baseline gap-3 min-w-0',
  brand: 'text-[21px] font-bold leading-none tracking-wide text-primary-text',
  tagline: 'text-[11px] font-bold tracking-label text-ink-3 hidden truncate sm:inline',
  nav: 'flex shrink-0 items-center gap-5 text-[12px]',
  navLink: 'font-bold text-ink-2 transition-colors hover:text-primary-text',
  navLinkWide: 'font-bold text-ink-2 transition-colors hover:text-primary-text hidden sm:inline',
};

/** The cross-links every page carries, in one order. The trailing action
 *  differs by context and is passed in by each side. */
export const NAV = [
  { href: '/articles.html', label: 'エリアガイド' },
  { href: '/rankings.html', label: 'ランキング' },
  { href: '/about.html', label: 'マプカチについて' },
];

/**
 * The site header. `cta` is the deep link back into the estimator, which each
 * page builds with its own utm campaign.
 */
export function siteHeader(cta, { ctaLabel = '地図で推定する' } = {}) {
  const links = NAV.map(
    (n) => `<a href="${n.href}" class="${HEADER.navLinkWide}">${n.label}</a>`,
  ).join('\n      ');
  return `<header class="${HEADER.bar}">
  <div class="${C.shell}">
  <div class="${HEADER.row}">
    <a href="/" class="${HEADER.brandWrap}">
      <span class="${HEADER.brand}">マプカチ</span>
      <span class="${HEADER.tagline}">地点から相場を推定する</span>
    </a>
    <nav class="${HEADER.nav}">
      ${links}
      <a href="${cta}" class="${C.btnQuiet}">${ctaLabel}</a>
    </nav>
  </div>
  </div>
</header>`;
}

/**
 * Breadcrumb trail. `trail` is [{label, href}] with the last entry current.
 */
export function breadcrumb(trail) {
  const parts = trail.map((t, i) =>
    i === trail.length - 1 || !t.href
      ? `<span class="text-ink-2">${t.label}</span>`
      : `<a href="${t.href}" class="${C.linkPlain}">${t.label}</a>`,
  );
  return `<nav class="mb-5 text-[12px] tracking-copy text-ink-3">${parts.join(
    '<span class="mx-1.5 text-rule-strong">›</span>',
  )}</nav>`;
}

/**
 * Closing call to action. Quiet by design: the page has already done the
 * arguing, and a shouting banner would undercut a report.
 */
export function ctaSection(cta, { heading, note, label = 'この地点で推定する' }) {
  return `<section class="${C.section} rounded-lg bg-tint-cool px-6 py-[clamp(2rem,4vw,3.5rem)] text-center">
  <h2 class="${C.h2}">${heading}</h2>
  ${note ? `<p class="mx-auto mt-4 max-w-xl ${C.body_}">${note}</p>` : ''}
  <a href="${cta}" class="${C.btn} mt-8">${label}</a>
</section>`;
}

/**
 * The footer. Carries the credit route required by the 不動産情報ライブラリ
 * terms (出典 + a link to the full credit page) and the statement that this is
 * an independent service, which is why it must appear on every generated page.
 */
export function siteFooter({ extra = '' } = {}) {
  return `<footer class="mt-14 border-t border-rule bg-sheet">
  <div class="${C.shell} py-10">
   <div class="${C.prose} text-[11px] leading-jp text-ink-3">
    <p>マプカチは個人が運営する参考価格の推定ツールです。国土交通省・自治体とは関係ありません。掲載の価格は機械学習による推定値であり、重要事項説明・契約・建築確認などの法定手続には使えません。</p>
    <p class="mt-2">出典: 国土交通省 不動産情報ライブラリ (<a href="https://www.reinfolib.mlit.go.jp/" rel="noreferrer" class="${C.linkPlain}">https://www.reinfolib.mlit.go.jp/</a>) ・ 国土数値情報 ・ 地理院タイル。マプカチが集計・編集加工しています。</p>
    <p class="mt-2">このサービスは、国土交通省の不動産情報ライブラリのAPI機能を使用していますが、提供情報の最新性、正確性、完全性等が保証されたものではありません。</p>
    ${extra ? `<p class="mt-2">${extra}</p>` : ''}
    <p class="mt-3"><a href="/about.html" class="${C.linkPlain}">マプカチについて</a><span class="mx-2 text-rule-strong">/</span><a href="/how-it-works.html" class="${C.linkPlain}">推定の仕組み</a><span class="mx-2 text-rule-strong">/</span><a href="/" class="${C.linkPlain}">地図で推定する</a></p>
   </div>
  </div>
</footer>`;
}

/**
 * A boxed aside. Two tones, and the choice between them is not decorative:
 *
 *   'alert' — a statement the reader must not miss, and which the 不動産情報
 *             ライブラリ terms require us to make plainly (this is not an
 *             official service; predictions are not the ministry's data).
 *             White sheet with an accent bar, so it sits forward of the page.
 *   'note'  — supporting disclosure that belongs beside the text rather than
 *             in front of it. Tinted, no bar, no shadow.
 *
 * Accent text on the warm tint measures 4.49:1, just under the 4.5 floor, so
 * the alert tone puts its accent in the bar and keeps the words in ink.
 */
export function callout(heading, bodyHtml, { tone = 'note' } = {}) {
  const box =
    tone === 'alert'
      ? 'rounded-lg border-l-4 border-accent bg-sheet p-5 shadow-sm'
      : 'rounded-lg bg-tint-cool p-5';
  return `<aside class="mt-6 ${box}">
  <p class="text-[14px] font-bold text-ink">${heading}</p>
  <div class="mt-2 space-y-2 text-[13px] leading-jp text-ink-2">${bodyHtml}</div>
</aside>`;
}

/**
 * Specification table: a label column and one or more value columns. Used for
 * the operator record and the model's data/back-test figures, which are the
 * pages where a reader is checking a specific fact rather than reading prose.
 * `head` is optional; without it the label column alone carries the meaning.
 */
export function specTable(rows, { head = null } = {}) {
  const thead = head
    ? `<thead><tr>${head.map((h) => `<th scope="col" class="${C.th}">${h}</th>`).join('')}</tr></thead>`
    : '';
  const body = rows
    .map(
      ([label, ...cells]) => `<tr>
      <th scope="row" class="${C.td} bg-sheet-sunk text-left font-bold text-ink align-top whitespace-nowrap">${label}</th>
      ${cells.map((c) => `<td class="${C.td} align-top">${c}</td>`).join('')}
    </tr>`,
    )
    .join('\n');
  return `<div class="${C.tableWrap}">
  <table class="${C.table}"><colgroup><col class="w-[9.5rem]" /></colgroup>${thead}<tbody class="${C.tbody}">${body}</tbody></table>
</div>`;
}

/**
 * Bulleted list in the body tone. The marker is a small ruled tick rather than
 * a filled disc — the same device the tables use for their rules, so a list
 * and a table read as the same kind of object.
 *
 * `size` picks between two fixed classes rather than taking a length. Tailwind
 * scans source text for literal class names, so a size interpolated into an
 * arbitrary-value class would never make it into the stylesheet.
 */
export function bullets(items, { size = 'sm' } = {}) {
  const text = size === 'xs' ? 'text-[12px]' : 'text-[13px]';
  return `<ul class="mt-4 space-y-1.5 ${text} leading-jp text-ink-2">
  ${items.map((t) => `<li class="relative pl-4 before:absolute before:left-0 before:top-[0.7em] before:h-1 before:w-1.5 before:rounded-sm before:bg-rule-strong">${t}</li>`).join('\n  ')}
</ul>`;
}

/**
 * Rank marker for a ranking table. The top three carry a filled marker;
 * everyone else is set plainly.
 *
 * An earlier version also shaded the bottom five in the warm tint this system
 * reserves for cautions, which read as an error state on what is just the
 * cheaper end of a price list.
 */
export function rankCell(rank) {
  const lead = rank <= 3 ? 'bg-primary text-sheet' : 'text-ink-3';
  return `<td class="px-3 py-2.5 text-center">
      <span class="${C.fig} inline-flex h-6 min-w-6 items-center justify-center rounded px-1.5 text-[12px] ${lead}">${rank}</span>
    </td>`;
}

/**
 * The headline price in a ranking table, with a rule under it whose length is
 * this value's share of the leader's.
 *
 * A rank column gives the order and hides the distance: 1位 and 23位 sit one
 * row apart but are a factor of three. The bar is the only thing in the table
 * that shows that spread, and it costs one element per row.
 *
 * `format` renders the number — the callers disagree about 万/億 wording.
 */
export function leadPriceCell(value, max, format) {
  const pct = max > 0 && value > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  return `<td class="relative px-3 py-2.5 text-right">
      <span class="${C.fig} text-[13px] text-ink">${format(value)}</span>
      ${pct ? `<span aria-hidden="true" class="absolute bottom-1 right-3 block h-[3px] max-w-[calc(100%-1.5rem)] rounded-sm bg-primary/35" style="width:${pct}%"></span>` : ''}
    </td>`;
}
